import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  en,
  es,
  fr,
  ja,
  localeNames,
  supportedLocales,
  translations,
  type SupportedLocale,
  type TranslationKey,
} from "@sportex100/shared/i18n/translations";
import { getDeviceLocale, resolveSupportedLocale } from "./locale";

export type LocalePreference = SupportedLocale | "device" | null;
export type LanguageOption = {
  value: Exclude<LocalePreference, null>;
  label: string;
};

const LANGUAGE_PREFERENCE_KEY = "settings.language.preference.v1";
const localePreferenceListeners = new Set<() => void>();

function interpolate(value: string, params?: Record<string, string | number>) {
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (match, key) => {
    const replacement = params[key];
    return replacement == null ? match : String(replacement);
  });
}

export function translate(
  locale: string | null | undefined,
  key: TranslationKey,
  params?: Record<string, string | number>,
) {
  const resolvedLocale = resolveSupportedLocale(locale);
  const value = translations[resolvedLocale][key] ?? en[key];
  return interpolate(value, params);
}

function isSupportedLocalePreference(value: string): value is SupportedLocale {
  return supportedLocales.some((supportedLocale) => supportedLocale === value);
}

function getStoredLanguagePreference(): LocalePreference {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(LANGUAGE_PREFERENCE_KEY);
  if (stored === "device" || (stored && isSupportedLocalePreference(stored))) {
    return stored;
  }
  return null;
}

function emitLanguagePreferenceChange() {
  localePreferenceListeners.forEach((listener) => listener());
}

function subscribeLanguagePreference(listener: () => void) {
  localePreferenceListeners.add(listener);
  if (typeof window !== "undefined") {
    window.addEventListener("storage", emitLanguagePreferenceChange);
  }
  return () => {
    localePreferenceListeners.delete(listener);
    if (typeof window !== "undefined" && localePreferenceListeners.size === 0) {
      window.removeEventListener("storage", emitLanguagePreferenceChange);
    }
  };
}

export function resolveEffectiveLocale(
  preference: LocalePreference,
  deviceLocale: string | null | undefined,
): SupportedLocale {
  if (preference && preference !== "device") {
    return preference;
  }
  return resolveSupportedLocale(deviceLocale);
}

export function getLanguageOptions(locale: SupportedLocale): LanguageOption[] {
  return [
    { value: "device", label: "Default" },
    ...supportedLocales.map((supportedLocale) => ({
      value: supportedLocale,
      label: localeNames[supportedLocale],
    })),
  ];
}

async function writeLanguagePreference(preference: Exclude<LocalePreference, null>) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LANGUAGE_PREFERENCE_KEY, preference);
  }
  emitLanguagePreferenceChange();
}

export function useLocalization() {
  const preference = useSyncExternalStore(
    subscribeLanguagePreference,
    getStoredLanguagePreference,
    () => null,
  );
  const locale = resolveEffectiveLocale(preference, getDeviceLocale());
  const setPreference = useCallback(
    async (nextPreference: Exclude<LocalePreference, null>) => {
      await writeLanguagePreference(nextPreference);
    },
    [],
  );

  return useMemo(() => {
    return {
      locale,
      preference,
      setPreference,
      t: (key: TranslationKey, params?: Record<string, string | number>) =>
        translate(locale, key, params),
      languageOptions: getLanguageOptions(locale),
    };
  }, [locale, preference, setPreference]);
}

export {
  en,
  es,
  fr,
  ja,
  localeNames,
  supportedLocales,
};

export type { SupportedLocale, TranslationKey };
